/* Серверный источник шаблонной «Поддержки»: опубликованная справка и статус, ИИ-агент и диктовка вопроса.
   Чат агента живёт в sessionStorage владельца (agent-model): намерение пишется до запроса, повтор идёт тем же ключом, неизвестный исход не создаёт второй вопрос. */
import { endpoints } from '../../api';
import { ApiError, uuid } from '../../api/client';
import type { ServiceStatus, SupportAction } from '../../api/help-content';
import { recordingWav } from '../tts/recording-wav';
import { agentGuid, readAgentChat, runAgentRequest, saveAgentChat, validateAgentResult, type AgentChat, type AgentResult, type AgentTurn } from './agent-model';
export type { AgentChat };
import { helpDocument, statusFresh, type HelpDocument } from './help-content';
import { helpRoute, navigateHelp } from './help-navigation';
import { uiFormat, uiText } from '../../i18n';

const dictationCycles = new Map<number, number>();
const DICTATION_MS = 30_000;
const DICTATION_BYTES = 10 * 1024 * 1024;
const dictationKey = (owner: number) => `steos:help:dictation:${owner}`;

export interface HelpStatusView {
  fresh: boolean;
  checkedAt: number | null;
  asOf: number | null;
  services: Array<{ id: string; title: string | null; state: string }>;
}

function statusView(source: ServiceStatus): HelpStatusView {
  const at = (v?: string | null) => (v && Number.isFinite(Date.parse(v)) ? Date.parse(v) : null);
  return {
    fresh: statusFresh(source),
    checkedAt: at(source.checked_at),
    asOf: at(source.as_of),
    services: (source.services ?? []).map(s => ({ id: s.id, title: s.title ?? null, state: s.state })),
  };
}

/** Ответ к ходу чата: чужой разговор или подменённое сообщение — отказ, а не тихая перезапись */
function apply(owner: number, requestId: string, response: AgentResult) {
  validateAgentResult(response);
  const chat = readAgentChat(owner);
  const turn = chat.turns.find(t => t.requestId === requestId);
  if (!turn) return chat;
  if (turn.input.conversation_id && turn.input.conversation_id !== response.conversation_id) throw new Error('support_response_conflict');
  if (turn.response && (turn.response.message_id !== response.message_id || turn.response.conversation_id !== response.conversation_id))
    throw new Error('support_response_conflict');
  turn.response = response;
  turn.phase = response.status === 'processing' ? 'processing' : 'done';
  chat.conversationId = response.conversation_id;
  saveAgentChat(chat);
  return chat;
}

function mark(owner: number, requestId: string, phase: AgentTurn['phase']) {
  const chat = readAgentChat(owner);
  const turn = chat.turns.find(t => t.requestId === requestId);
  if (turn) turn.phase = phase;
  saveAgentChat(chat);
  return chat;
}

/** Запись с микрофона: start — разрешение и запись, stop — файл (не дольше 30 с, дальше останавливается сама) */
function recorder(onAuto: () => void) {
  let rec: MediaRecorder | null = null,
    stream: MediaStream | null = null,
    timer = 0,
    cancelled = false;
  const chunks: Blob[] = [];
  const release = () => {
    clearTimeout(timer);
    stream?.getTracks().forEach(t => t.stop());
  };
  return {
    async start() {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') throw new Error('mic_unavailable');
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (cancelled) { release(); throw new Error('recording_cancelled'); }
      try {
        const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'].find(t => MediaRecorder.isTypeSupported(t));
        rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
        rec.ondataavailable = e => {
          if (e.data.size) chunks.push(e.data);
        };
        rec.start();
        timer = window.setTimeout(onAuto, DICTATION_MS);
      } catch (e) {
        release();
        throw e;
      }
    },
    stop(): Promise<File | null> {
      return new Promise(resolve => {
        if (!rec || rec.state !== 'recording') {
          release();
          resolve(null);
          return;
        }
        const r = rec;
        r.onstop = () => {
          release();
          const type = r.mimeType.split(';')[0] || 'audio/webm';
          resolve(chunks.length ? new File(chunks, `dictation.${type === 'audio/mp4' ? 'm4a' : 'webm'}`, { type }) : null);
        };
        r.stop();
      });
    },
    cancel() {
      cancelled = true;
      if (rec && rec.state === 'recording') {
        rec.onstop = null;
        rec.stop();
      }
      release();
    },
  };
}

/** Сервис с перебоями: название строки «Статуса» и состояние */
export interface BadService {
  title: string;
  state: 'slow' | 'down';
}
/** Строка замедления вверху «Помощи» и итог «Статуса» (SPEC-6 §9.4): один сервис с перебоями — по названию, как у шаблона
    («Видео медленнее обычного», «Видео работает медленнее обычного»); несколько — общими словами */
export function statusHeadline(bad: BadService[]): { alert: string; sum: string } {
  if (bad.length !== 1) return { alert: uiText('Некоторые сервисы работают медленнее обычного'), sum: uiText('Часть сервисов работает с перебоями') };
  const { title, state } = bad[0];
  /* «Кружки и говорящие видео» — множественное число по первому слову; фразы — шаблонами словаря профиля, название — служебное имя через словарь */
  const many = /[иы]$/i.test(title.split(' ')[0]);
  const name = uiText(title);
  if (state === 'down') {
    const text = uiFormat(many ? '{0} не работают' : '{0} не работает', name);
    return { alert: text, sum: text };
  }
  return { alert: uiFormat('{0} медленнее обычного', name), sum: uiFormat(many ? '{0} работают медленнее обычного' : '{0} работает медленнее обычного', name) };
}

/** Цена инструкции сервера частями, как цены шаблона: числа отдельно (шапка инструкции рисует их моноширинными, prHTML), текст между ними — как есть */
export function priceParts(price: string): string[] {
  return price.split(/(\d+(?:[.,]\d+)?)/).filter(Boolean);
}

export const helpServer = {
  headline: statusHeadline,
  priceParts,
  async help(): Promise<HelpDocument | null> {
    return helpDocument(await endpoints.helpContent.help());
  },
  async status(): Promise<HelpStatusView> {
    return statusView(await endpoints.helpContent.status());
  },
  owner: async () => (await endpoints.me()).user.id,
  chat: (owner: number): AgentChat => readAgentChat(owner),
  /** Новый вопрос записывается ходом до POST: его ключ и есть ключ идемпотентности */
  begin(owner: number, message: string) {
    const chat = readAgentChat(owner);
    const turn: AgentTurn = {
      requestId: uuid(),
      input: { ...(chat.conversationId ? { conversation_id: chat.conversationId } : {}), message },
      phase: 'unknown',
    };
    chat.turns.push(turn);
    saveAgentChat(chat);
    return turn.requestId;
  },
  /** Отправить записанный ход (и повтор после обрыва — тем же ключом). Отказ по самому вопросу — ход в архив, его можно задать иначе */
  async send(owner: number, requestId: string) {
    const turn = readAgentChat(owner).turns.find(t => t.requestId === requestId);
    if (!turn) throw new Error('support_turn_missing');
    try {
      return apply(owner, requestId, await runAgentRequest(owner, turn, endpoints.helpContent));
    } catch (e) {
      if (e instanceof ApiError && e.status === 422 && e.code === 'support_message_invalid') {
        const chat = readAgentChat(owner);
        chat.turns = chat.turns.filter(t => t.requestId !== requestId);
        chat.archived = [...chat.archived, { requestId, phase: 'rejected' as const, conversationId: turn.input.conversation_id }].slice(-40);
        saveAgentChat(chat);
      } else if (e instanceof ApiError && e.code === 'support_outcome_uncertain') mark(owner, requestId, 'uncertain');
      throw e;
    }
  },
  async poll(owner: number, requestId: string, messageId: string) {
    try {
      return apply(owner, requestId, await endpoints.helpContent.agentMessage(messageId));
    } catch (e) {
      if (e instanceof ApiError && e.code === 'support_outcome_uncertain') mark(owner, requestId, 'uncertain');
      throw e;
    }
  },
  async rate(owner: number, requestId: string, value: -1 | 0 | 1) {
    const turn = readAgentChat(owner).turns.find(t => t.requestId === requestId);
    if (!turn?.response || turn.phase !== 'done') return;
    await endpoints.helpContent.rate({ conversation_id: turn.response.conversation_id, message_id: turn.response.message_id, value });
    const chat = readAgentChat(owner);
    const item = chat.turns.find(t => t.requestId === requestId);
    if (item) item.rating = value;
    saveAgentChat(chat);
  },
  /** Новый чат: прежние ходы — в архив (их ключи не переиспользуются) */
  reset(owner: number) {
    const chat = readAgentChat(owner);
    saveAgentChat({
      owner,
      turns: [],
      archived: [
        ...chat.archived,
        ...chat.turns.map(t => ({ requestId: t.requestId, conversationId: t.response?.conversation_id, messageId: t.response?.message_id, phase: t.phase })),
      ].slice(-40),
    });
  },
  recorder,
  /** Диктовка: запись → WAV → загрузка → распознавание. Загрузка и ключ помнятся до ответа: повтор проверяет прежний запрос, а не распознаёт заново */
  async dictate(owner: number, file: File | null) {
    const cycle = dictationCycles.get(owner) ?? 0;
    const current = () => { if ((dictationCycles.get(owner) ?? 0) !== cycle) throw new Error('dictation_cancelled'); };
    let intent = readIntent(owner);
    if (!intent) {
      if (!file) throw new Error('dictation_empty');
      const wav = await recordingWav(file, DICTATION_MS);
      current();
      if (wav.size > DICTATION_BYTES) throw new Error('dictation_file_too_large');
      const upload = await endpoints.uploadFile(wav, { purpose: 'order_input', kind: 'audio', origin: 'file' }, uuid());
      current();
      if (upload.status !== 'ready' || upload.purpose !== 'order_input') throw new Error('dictation_upload_not_ready');
      intent = { owner, key: uuid(), upload: upload.id };
      sessionStorage.setItem(dictationKey(owner), JSON.stringify(intent));
    }
    try {
      const receipt = await endpoints.helpContent.dictation(intent.upload, intent.key);
      current();
      if (typeof receipt.text !== 'string' || receipt.text.length > 4000) throw new Error('dictation_receipt_invalid');
      sessionStorage.removeItem(dictationKey(owner));
      return receipt.text.slice(0, 300);
    } catch (e) {
      if ((dictationCycles.get(owner) ?? 0) === cycle && e instanceof ApiError && e.status === 422 && /^support_dictation_(audio|duration)_invalid$|^support_dictation_upload_expired$/.test(e.code))
        sessionStorage.removeItem(dictationKey(owner));
      throw e;
    }
  },
  cancelDictation(owner: number) { dictationCycles.set(owner, (dictationCycles.get(owner) ?? 0) + 1); sessionStorage.removeItem(dictationKey(owner)); },
  dictationPending: (owner: number) => !!readIntent(owner),
  dictationError(e: unknown) {
    if (e instanceof Error && e.message === 'mic_unavailable') return uiText('Микрофон здесь недоступен');
    if (e instanceof DOMException) return uiText('Нет доступа к микрофону');
    if (e instanceof ApiError && e.code === 'support_dictation_duration_invalid') return uiText('Нужна запись от 1 до 30 секунд');
    if (e instanceof ApiError && e.code === 'support_dictation_audio_invalid') return uiText('Не удалось разобрать запись');
    return uiText('Диктовка не распозналась. Нажмите микрофон, чтобы проверить ещё раз');
  },
  agentError(e: unknown) {
    if (e instanceof ApiError && e.code === 'support_message_invalid') return uiText('Вопрос не подошёл. Сформулируйте иначе');
    if (e instanceof ApiError && e.code === 'support_outcome_uncertain') return uiText('Ответ не подтвердился. Напишите в поддержку');
    return uiText('Ответ не пришёл. Проверьте ещё раз');
  },
  canNavigate: (action: SupportAction) => !!helpRoute(action),
  navigate: (action: SupportAction) => navigateHelp(action),
};

function readIntent(owner: number): { owner: number; key: string; upload: string } | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(dictationKey(owner)) || 'null');
    return v && v.owner === owner && agentGuid(v.key) && agentGuid(v.upload) ? v : null;
  } catch {
    return null;
  }
}

export type HelpServer = typeof helpServer;
